<script setup lang="ts">
/**
 * UI-kit component
 * Category: Paginations
 * Name: AppPagination
 */
import type { AppPaginationItem, AppPaginationProps } from '../types/interfaces'

import { AppText } from '@shared/ui/typography'
import { AppTableSelect } from '@shared/ui/selects'

import { useAppPagination } from '../composables/use-app-pagination.hook'
import AppPaginationLink from './AppPaginationLink.vue'

defineOptions({
  name: 'AppPagination',
})

const props = withDefaults(defineProps<AppPaginationProps>(), {
  perPageOptions: () => [15, 25, 50, 100, 150],
  disabled: false,
  position: 'bottom',
})

const emit = defineEmits<{
  'change:page': [page: number]
  'change:per-page': [perPage: number]
}>()

const {
  items,
  perPageSelectOptions,
  title,
} = useAppPagination({ props })

const handleItemClick = (item: AppPaginationItem): void => {
  if (item.page === null || item.disabled) return
  emit('change:page', item.page)
}

const handlePerPageChange = (value: number): void => {
  if (value === props.meta.per_page) return
  emit('change:per-page', value)
}
</script>

<template>
  <div :class="['app-pagination', `app-pagination--${position}`]">
    <div class="app-pagination__general">
      <AppText
        name="body2"
        color="slate-700"
      >
        Rows per page:
      </AppText>
      <AppTableSelect
        :model-value="meta.per_page"
        :options="perPageSelectOptions"
        :disabled="disabled"
        @update:model-value="handlePerPageChange"
      />
    </div>
    <AppText
      class="app-pagination__title"
      name="body2"
      color="slate-700"
    >
      {{ title }}
    </AppText>
    <nav class="app-pagination__nav">
      <AppPaginationLink
        v-for="(item, index) in items"
        :key="index"
        :item="item"
        @click="handleItemClick(item)"
      />
    </nav>
  </div>
</template>

<style lang="scss" scoped src="../styles/_app-pagination.scss" />
